import { useState } from "react";
import { motion } from "framer-motion";
import { Check, ArrowUpRight } from "lucide-react";

const SKOOL_URL = "https://www.skool.com/sircs-reprogramacion-mental-8281";

const PLANS = {
  monthly: {
    id: "monthly",
    label: "Mensual",
    amount: "$47",
    period: "/ mes",
    note: "Cancela cuando quieras",
    badge: null,
    pixelValue: 47,
  },
  annual: {
    id: "annual",
    label: "Anual",
    amount: "$299",
    period: "/ año",
    note: "Ahorra $265 al año · ~47% menos",
    badge: "Más popular",
    pixelValue: 299,
  },
};

const INCLUDES = [
  "Acceso completo a la comunidad privada en Skool",
  "Protocolos de reprogramación paso a paso",
  "Sesiones, mentoría y debrief semanal con Santi",
  "Bibliotecas de patrones, creencias y disparadores",
  "Comunidad activa 24/7 para acompañarte en cada bucle",
  "Cancela cuando quieras · sin permanencia",
];

export default function Pricing() {
  const [planId, setPlanId] = useState("annual");
  const plan = PLANS[planId];

  const trackCTA = () => {
    if (typeof window !== "undefined" && window.fbq) {
      window.fbq("track", "InitiateCheckout", {
        value: plan.pixelValue,
        currency: "USD",
        content_name: `SIRCS ${plan.label}`,
        content_category: "skool_membership",
      });
    }
  };

  return (
    <section
      id="precio"
      data-testid="pricing-section"
      className="surface-dark relative py-24 sm:py-32 overflow-hidden"
    >
      <div className="warm-halo" aria-hidden />

      <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
        <div className="mb-12">
          <div className="flex items-center gap-3 mb-8">
            <span className="h-px w-10 bg-pearl/40" />
            <span className="font-body text-[11px] tracking-[0.28em] uppercase text-pearl/70">
              04 · Acceso
            </span>
          </div>
          <motion.h2
            initial={{ opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            data-testid="pricing-headline"
            className="font-display text-4xl sm:text-5xl lg:text-6xl font-normal tracking-[-0.02em] leading-[1.0] text-cream"
          >
            Un precio. <em className="italic text-pearl">Cero excusas.</em>
          </motion.h2>
        </div>

        {/* Toggle */}
        <div
          className="inline-flex p-1 rounded-full bg-[#121820] border border-[#1E252F] mb-10"
          role="tablist"
          data-testid="pricing-toggle"
        >
          {["monthly", "annual"].map((id) => {
            const active = planId === id;
            return (
              <button
                key={id}
                onClick={() => setPlanId(id)}
                data-testid={`pricing-toggle-${id}`}
                role="tab"
                aria-selected={active}
                className={`px-6 py-2.5 rounded-full font-body text-[11px] uppercase tracking-[0.22em] transition-all ${
                  active ? "bg-pearl text-ink" : "text-pearl/70 hover:text-cream"
                }`}
              >
                {PLANS[id].label}
                {id === "annual" && (
                  <span className={`ml-2 normal-case text-[10px] tracking-normal ${active ? "text-warm" : "text-warm/80"}`}>
                    -47%
                  </span>
                )}
              </button>
            );
          })}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 28 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="relative bg-[#121820] rounded-2xl border border-[#1E252F] grid grid-cols-1 lg:grid-cols-12 overflow-hidden"
          data-testid="pricing-card"
        >
          {/* Left */}
          <div className="lg:col-span-7 p-8 lg:p-12 border-b lg:border-b-0 lg:border-r border-[#1E252F] relative">
            {plan.badge && (
              <span
                data-testid="pricing-badge"
                className="absolute top-6 right-6 lg:top-10 lg:right-10 bg-warm text-ink px-4 py-1.5 rounded-full font-body text-[10px] uppercase tracking-[0.18em]"
              >
                {plan.badge}
              </span>
            )}
            <div className="font-body text-[10px] uppercase tracking-[0.28em] text-pearl/60 mb-4">
              Membresía {plan.label.toLowerCase()}
            </div>
            <div className="flex items-end gap-3 mb-3">
              <span
                data-testid="pricing-amount"
                className="font-display text-7xl sm:text-8xl font-normal tracking-[-0.02em] leading-none text-cream"
              >
                {plan.amount}
              </span>
              <span className="font-body text-sm text-pearl/60 pb-3">{plan.period}</span>
            </div>
            <p data-testid="pricing-note" className="font-body text-[12px] uppercase tracking-[0.18em] text-warm mb-8">
              {plan.note}
            </p>
            <p className="font-body text-base sm:text-lg text-pearl/80 max-w-md leading-relaxed">
              Empieza con una <span className="text-cream">prueba gratuita</span> dentro de la
              comunidad. Si no es para ti, cancela en un clic.
            </p>

            <div className="mt-10 flex flex-col sm:flex-row gap-3">
              <a
                href={SKOOL_URL}
                target="_blank"
                rel="noopener noreferrer"
                onClick={trackCTA}
                data-testid="pricing-cta-primary"
                className="group btn-pearl inline-flex items-center justify-between gap-4 px-6 py-4 rounded-full font-body text-[12px] uppercase tracking-[0.22em]"
              >
                Comenzar prueba gratis
                <ArrowUpRight size={18} className="group-hover:rotate-45 transition-transform" />
              </a>
              <a
                href={SKOOL_URL}
                target="_blank"
                rel="noopener noreferrer"
                onClick={trackCTA}
                data-testid="pricing-cta-secondary"
                className="btn-outline-pearl inline-flex items-center justify-center gap-2 px-6 py-4 rounded-full font-body text-[12px] uppercase tracking-[0.22em]"
              >
                Ver Skool
              </a>
            </div>
          </div>

          {/* Right */}
          <div className="lg:col-span-5 p-8 lg:p-12">
            <div className="font-body text-[10px] uppercase tracking-[0.28em] text-pearl/60 mb-6">
              Incluye
            </div>
            <ul className="space-y-4">
              {INCLUDES.map((it, i) => (
                <li key={i} className="flex items-start gap-3 text-pearl/85">
                  <Check size={16} strokeWidth={1.5} className="text-warm mt-1 shrink-0" />
                  <span className="font-body text-sm sm:text-base leading-relaxed">{it}</span>
                </li>
              ))}
            </ul>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
